<template>
    <div class="block-editor zoho-create-record">
        <div class="wrapper">
            <section>
                <div class="title">{{ $t('auto.zohoCreateRecordZohoModuleName') }}</div>
                <CustomPicker
                    v-model="selectedModule"
                    :items="modules"
                    idKey="api_name"
                    labelKey="plural_label"
                    title="Select Module"
                />
            </section>

            <section>
                <div class="title">{{ $t('auto.zohoCreateRecordStandardFields') }}</div>
                <div class="field-picker-wrapper">
                    <CustomPicker
                        v-model="selectedStandardField"
                        :items="availableStandardFields"
                        idKey="api_name"
                        labelKey="field_label"
                        title="Select Standard Field"
                    />
                    <SupsisButton @click="addField(selectedStandardField)">
                        <i class="material-icons">add</i>
                        <span>{{ $t('auto.zohoCreateRecordAddField') }}</span>
                    </SupsisButton>
                </div>
            </section>

            <section>
                <div class="title">{{ $t('auto.zohoCreateRecordCustomFields') }}</div>
                <div class="field-picker-wrapper">
                    <CustomPicker
                        v-model="selectedCustomField"
                        :items="availableCustomFields"
                        idKey="api_name"
                        labelKey="field_label"
                        title="Select Custom Field"
                    />
                    <SupsisButton @click="addField(selectedCustomField)">
                        <i class="material-icons">add</i>
                        <span>{{ $t('auto.zohoCreateRecordAddField') }}</span>
                    </SupsisButton>
                </div>
            </section>

            <section v-for="(field, fieldApiName) in clone.fields" :key="fieldApiName" class="field-section">
                <i
                    v-if="!field.fieldMeta.system_mandatory"
                    class="material-icons remove-field-icon"
                    @click="removeField(fieldApiName)"
                    title="Alanı Kaldır"
                >
                    close
                </i>
                <div class="title">{{ field.fieldMeta.field_label }}</div>
                <CustomPicker
                    v-if="fieldApiName === 'Owner'"
                    v-model="field.value"
                    :items="zohoUsers"
                    idKey="id"
                    labelKey="name"
                    :title="field.fieldMeta.field_label"
                />
                <div class="text-editor-wrapper" v-else-if="field.fieldMeta.data_type !== 'picklist'">
                    <TiptapEditor
                        :placeholder="field.fieldMeta.field_label"
                        :show-attributes="true"
                        :show-emoji="false"
                        v-model="field.value"
                        automation-mode
                    />
                </div>
                <CustomPicker
                    v-else
                    v-model="field.value"
                    :items="field.fieldMeta.pick_list_values"
                    idKey="actual_value"
                    labelKey="display_value"
                    :title="field.fieldMeta.field_label"
                />
            </section>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import { ZOHO_API } from "../../../../../../../api/zoho";

const log = log2("ZohoCreateRecord");
export default {
    name: "ZohoCreateRecord",
    components: { TiptapEditor, CustomPicker, SupsisButton },
    props: ["value"],
    data() {
        return {
            modules: [],
            selectedModule: null,
            clone: _.cloneDeep(this.value) || {
                module: "",
                fields: {},
            },
            selectedStandardField: null,
            selectedCustomField: null,
            allFields: [],
            zohoUsers: [],
        };
    },
    computed: {
        standardFields() {
            return Object.values(this.clone.fields || {}).filter((field) => !field.fieldMeta.custom_field);
        },
        customFields() {
            return Object.values(this.clone.fields || {}).filter((field) => field.fieldMeta.custom_field);
        },
        availableStandardFields() {
            const addedFields = Object.keys(this.clone.fields || {});
            return this.allFields.filter((field) => !field.custom_field && !addedFields.includes(field.api_name));
        },
        availableCustomFields() {
            const addedFields = Object.keys(this.clone.fields || {});
            return this.allFields.filter((field) => field.custom_field && !addedFields.includes(field.api_name));
        },
    },

    methods: {
        async fetchModules() {
            const modules = await ZOHO_API.listModules();
            this.modules = modules;
        },
        async fetchUsers() {
            const users = await ZOHO_API.listAllUsers();
            this.zohoUsers = users;
        },
        async onModuleChange(module) {
            this.selectedModule = module;
            const isModuleChanged = module !== this.clone.module;
            
            if (isModuleChanged) {
                this.clone.module = module;
                this.clone.fields = {};
                this.selectedStandardField = null;
                this.selectedCustomField = null;
            }

            const fields = await ZOHO_API.listModuleFields(module);
            this.allFields = fields;
            
            if (isModuleChanged) {
                fields.forEach((field) => {
                    if (field.system_mandatory) {
                        this.clone.fields[field.api_name] = {
                            fieldApiName: field.api_name,
                            value: null,
                            fieldMeta: field,
                        };
                    }
                });
            }
        },
        addField(fieldApiName) {
            if (fieldApiName) {
                const fieldMeta = this.allFields.find((f) => f.api_name === fieldApiName);
                if (this.clone.fields[fieldApiName]) {
                    return;
                }
                this.$set(this.clone.fields, fieldApiName, {
                    fieldApiName: fieldApiName,
                    value: null,
                    fieldMeta: fieldMeta,
                });
                this.clone = _.cloneDeep(this.clone);
                this.selectedStandardField = null;
                this.selectedCustomField = null;
            }
        },
        removeField(fieldApiName) {
            this.$delete(this.clone.fields, fieldApiName);
            this.clone = _.cloneDeep(this.clone);
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
        selectedModule: {
            handler(newValue) {
                this.onModuleChange(newValue);
            },
        },
    },
    created() {
        this.fetchModules();
        this.fetchUsers();
        if (this.value.module) {
            this.onModuleChange(this.value.module);
        }
    },
};
</script>

<style lang="scss">
.block-editor.zoho-create-record {
    section {
        margin-bottom: 10px;
    }

    .wrapper {
        padding: 30px;

        .title {
            margin-bottom: 10px;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .text-editor-wrapper {
            border-radius: 0px;
            border: 1px solid #fff;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }
        }
    }

    .field-picker-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .field-section {
        position: relative;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        margin-bottom: 15px;

        &:hover .remove-field-icon {
            opacity: 1;
        }
    }

    .remove-field-icon {
        position: absolute;
        top: 10px;
        right: 10px;
        font-size: 20px;
        color: #8c8c8c;
        cursor: pointer;
        opacity: 0.5;
        transition: all 0.2s ease;
        z-index: 10;

        &:hover {
            color: #ff4d4f;
            opacity: 1;
            transform: scale(1.1);
        }
    }
}
</style>
